import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { StoryFrameCanvas } from "@/components/story/StoryFrameCanvas";
import { FourPaths } from "@/components/sections/FourPaths";
import { LanguageToggle } from "@/components/LanguageToggle";
import { SoundToggle } from "@/components/SoundToggle";
import { AudioTrack } from "@/components/AudioTrack";
import { IntroLoader } from "@/components/IntroLoader";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent, campaignAudioSrc } from "@/api/client";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_AUDIO = "/audio/website-ambience.mp3";

const overlayClasses = {
  left: "bottom-20 left-0 right-0 sm:bottom-auto sm:top-1/2 sm:right-auto sm:-translate-y-1/2 text-left",
  center:
    "top-1/2 left-0 right-0 -translate-y-1/2 text-center items-center flex flex-col",
  bottom: "bottom-16 left-0 right-0 text-center items-center flex flex-col",
};

const overlayOpacity = (p) => {
  // Full opacity from the start of the scene (no slow fade-in); only fade out
  // near the end so the reading window stays long and legible.
  if (p > 0.8) return Math.max(0, (1 - p) / 0.2);
  return 1;
};

const StorySectionBlock = ({ section, lang, onProgress }) => {
  const [p, setP] = useState(0);
  const framesBase = `${BACKEND}/api/media/${section.frames.storage_prefix}`;
  const o = overlayOpacity(p);
  const pos = overlayClasses[section.overlay_position] || overlayClasses.left;
  const headline =
    lang === "hi" ? section.headline_hi || section.headline : section.headline;
  const body = lang === "hi" ? section.body_hi || section.body : section.body;

  return (
    <section
      id={`story-sec-${section.id}`}
      data-testid={`story-section-${section.id}`}
      className="relative h-[calc(var(--vph)*3.2)]"
    >
      <div className="sticky top-0 h-[var(--vph)] w-full overflow-hidden">
        <StoryFrameCanvas
          framesBaseUrl={framesBase}
          prefix={section.frames.prefix}
          pad={section.frames.pad}
          total={section.frames.total}
          preset={section.animation_preset}
          sectionSelector={`#story-sec-${section.id}`}
          onProgress={(v) => {
            setP(v);
            onProgress && onProgress(v);
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070b18] via-transparent to-[#070b18]/50" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#070b18]/70 via-transparent to-transparent" />

        <div className="pointer-events-none absolute inset-0 px-6 sm:px-12 lg:px-24">
          <div className="relative h-full">
            <div
              className={`absolute max-w-xl ${pos}`}
              style={{ visibility: o < 0.02 ? "hidden" : "visible" }}
            >
              <div
                style={{
                  opacity: o,
                  transform: `translateY(${(1 - o) * 26}px)`,
                }}
              >
                {headline && (
                  <h2 className="whitespace-pre-line font-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                    {headline}
                  </h2>
                )}
                {body && (
                  <p className="mt-4 max-w-md text-sm text-amber-50/70 sm:text-base md:text-lg">
                    {body}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function CampaignStory({ campaign }) {
  const { lang, setLang } = useLanguage();
  const [showPaths, setShowPaths] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [entered, setEntered] = useState(false);
  const firstRef = useRef(null);
  const sections = campaign.story_sections || [];
  const audioSrc = campaignAudioSrc(campaign) || DEFAULT_AUDIO;

  const introUrls = (() => {
    const f = sections[0]?.frames;
    if (!f || !f.total) return [];
    return Array.from(
      { length: f.total },
      (_, i) =>
        `${BACKEND}/api/media/${f.storage_prefix}/${f.prefix}${String(
          i + 1
        ).padStart(f.pad, "0")}.jpg`
    );
  })();

  const handleEnter = (chosenLang) => {
    if (chosenLang) setLang(chosenLang);
    setAudioOn(true);
    setEntered(true);
    window.scrollTo({ top: 0 });
  };

  const brand =
    (lang === "hi"
      ? campaign.branding?.display_name_hi
      : campaign.branding?.display_name_en) || campaign.temple_name;

  const heroHeadline =
    (lang === "hi"
      ? sections[0]?.headline_hi || sections[0]?.headline
      : sections[0]?.headline) || campaign.temple_name;

  useEffect(() => {
    if (campaign.slug) trackEvent(campaign.slug, "website_visit");
  }, [campaign.slug]);

  const begin = () => firstRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleLastProgress = (v) => {
    if (v > 0.6 && !showPaths) {
      setShowPaths(true);
      if (campaign.slug) trackEvent(campaign.slug, "journey_completed");
    }
  };

  return (
    <div
      className={`relative bg-[#070b18] text-white antialiased ${
        lang === "hi" ? "lang-hi" : ""
      }`}
      data-testid="campaign-story-engine"
    >
      {!entered && <IntroLoader onEnter={handleEnter} urls={introUrls} />}
      <header className="fixed inset-x-0 top-0 z-50 flex items-center justify-between px-5 py-4 md:px-10 md:py-6">
        <span
          data-testid="story-brand"
          className="font-display text-base tracking-[0.15em] text-amber-100 md:text-lg"
        >
          {brand}
        </span>
        <div className="flex items-center gap-2">
          <SoundToggle on={audioOn} toggle={() => setAudioOn((v) => !v)} />
          <LanguageToggle />
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex h-[var(--vph)] flex-col items-center justify-center px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-4 text-xs uppercase tracking-[0.4em] text-amber-300/80 sm:text-sm"
        >
          {brand}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="max-w-4xl font-display text-4xl leading-[1.03] text-white sm:text-5xl lg:text-6xl"
        >
          {heroHeadline}
        </motion.h1>
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          onClick={begin}
          data-testid="story-begin-btn"
          className="group mt-10 inline-flex flex-col items-center gap-2 text-sm tracking-[0.25em] text-amber-100/70 transition-colors hover:text-amber-200"
        >
          BEGIN
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </motion.button>
      </section>

      {/* Story sections */}
      {sections.map((s, i) => (
        <div key={s.id} ref={i === 0 ? firstRef : undefined}>
          <StorySectionBlock
            section={s}
            lang={lang}
            onProgress={i === sections.length - 1 ? handleLastProgress : undefined}
          />
        </div>
      ))}

      {/* CTAs */}
      {(showPaths || sections.length === 0) && <FourPaths campaign={campaign} />}

      <AudioTrack src={audioSrc} enabled={audioOn} />
    </div>
  );
}
